import React from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import useMobile from '../hooks/useMobile';
import ChatbotSection from '../components/ChatbotSection';

const SupportPage = () => {
  const isMobile = useMobile();

  return (
    <div className="fade-in" style={{ padding: '60px 0' }}>
      <div className="container">
        <h1 style={{ 
          textAlign: 'center', 
          fontSize: isMobile ? '2.5rem' : '3rem', 
          marginBottom: '60px' 
        }}>
          Support Center
        </h1>

        {/* AI Chatbot Section */}
        <ChatbotSection onStartChat={() => {
          // This will trigger the chatbot to open
          // We'll implement this after adding the chatbot to App.js
          const chatButton = document.querySelector('[data-chatbot-trigger]');
          if (chatButton) chatButton.click();
        }} />

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '40px'
        }}>
          {/* Contact Information */}
          <div style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '16px',
            boxShadow: '0 5px 20px rgba(0,0,0,0.08)'
          }}>
            <h2 style={{ fontSize: '2rem', marginBottom: '30px' }}>Contact Information</h2>
            <div style={{ marginBottom: '30px', display: 'flex', alignItems: 'center' }}>
              <Phone size={24} color="#7c3aed" style={{ marginRight: '15px' }} />
              <div>
                <p style={{ margin: 0, fontWeight: 'bold' }}>Phone Support</p>
                <p style={{ margin: 0, color: '#6b7280' }}>1-800-KRAFTY-1 (1-800-572-3891)</p>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#9ca3af' }}>Mon-Fri 9AM-6PM PST</p>
              </div>
            </div>
            <div style={{ marginBottom: '30px', display: 'flex', alignItems: 'center' }}>
              <Mail size={24} color="#7c3aed" style={{ marginRight: '15px' }} />
              <div>
                <p style={{ margin: 0, fontWeight: 'bold' }}>Email Support</p>
                <p style={{ margin: 0, color: '#6b7280' }}>support@kraftyrestaurants.com</p>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#9ca3af' }}>Response within 24 hours</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <MapPin size={24} color="#7c3aed" style={{ marginRight: '15px' }} />
              <div>
                <p style={{ margin: 0, fontWeight: 'bold' }}>Headquarters</p>
                <p style={{ margin: 0, color: '#6b7280' }}>San Francisco, CA</p>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#9ca3af' }}>Remote support only</p>
              </div>
            </div>
          </div>

          {/* Quick Links & Resources */}
          <div style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '16px',
            boxShadow: '0 5px 20px rgba(0,0,0,0.08)'
          }}>
            <h2 style={{ fontSize: '2rem', marginBottom: '30px' }}>Quick Resources</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <button style={{
                backgroundColor: '#f3f4f6',
                border: 'none',
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}>
                <h3 style={{ margin: '0 0 5px 0' }}>Getting Started Guide</h3>
                <p style={{ margin: 0, color: '#6b7280' }}>Everything you need to launch your app</p>
              </button>
              <button style={{
                backgroundColor: '#f3f4f6',
                border: 'none',
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}>
                <h3 style={{ margin: '0 0 5px 0' }}>Video Tutorials</h3>
                <p style={{ margin: 0, color: '#6b7280' }}>Step-by-step walkthroughs</p>
              </button>
              <button style={{
                backgroundColor: '#f3f4f6',
                border: 'none',
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}>
                <h3 style={{ margin: '0 0 5px 0' }}>API Documentation</h3>
                <p style={{ margin: 0, color: '#6b7280' }}>For advanced integrations</p>
              </button>
              <button style={{
                backgroundColor: '#f3f4f6',
                border: 'none',
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}>
                <h3 style={{ margin: '0 0 5px 0' }}>Best Practices</h3>
                <p style={{ margin: 0, color: '#6b7280' }}>Tips to maximize your success</p>
              </button>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div style={{
          marginTop: '60px',
          backgroundColor: '#f9fafb',
          padding: isMobile ? '40px 20px' : '60px',
          borderRadius: '16px'
        }}>
          <h2 style={{ 
            fontSize: isMobile ? '2rem' : '2.5rem', 
            marginBottom: '40px',
            textAlign: 'center' 
          }}>
            Frequently Asked Questions
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '30px',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {[
              {
                q: "How long does app development take?",
                a: "Typically 4-6 weeks from sign-up to launch. We handle all the technical work while you focus on your restaurant."
              },
              {
                q: "What's included in the 5% platform fee?",
                a: "Everything! App hosting, updates, support, new features, and maintenance. Payment processing fees are separate."
              },
              {
                q: "Can I update my menu anytime?",
                a: "Absolutely! Use our management app to instantly update menu items, prices, availability, and more."
              },
              {
                q: "Do you handle app store submission?",
                a: "Yes, we handle the entire submission process for both Apple App Store and Google Play Store."
              },
              {
                q: "What payment methods are supported?",
                a: "We support all major credit cards, Apple Pay, and Google Pay through Stripe's secure processing."
              },
              {
                q: "Can I send push notifications?",
                a: "Yes! Send unlimited push notifications for orders, promotions, and updates directly to your customers."
              },
              {
                q: "Is there a contract or commitment?",
                a: "No long-term contracts. You only pay the 5% fee on orders you receive. Cancel anytime."
              },
              {
                q: "Do I need technical knowledge?",
                a: "Not at all! Our platform is designed to be simple and intuitive. Plus, our support team is always here to help."
              }
            ].map((faq, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '12px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
              }}>
                <h3 style={{ marginBottom: '15px', color: '#1a1a1a' }}>{faq.q}</h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6' }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div style={{
          marginTop: '60px',
          textAlign: 'center',
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ marginBottom: '20px' }}>Still Have Questions?</h2>
          <p style={{ 
            fontSize: '1.2rem', 
            color: '#6b7280', 
            marginBottom: '30px' 
          }}>
            Our team is here to help you succeed
          </p>
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button style={{
              backgroundColor: '#7c3aed',
              color: 'white',
              border: 'none',
              padding: '14px 30px',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <Phone size={20} />
              Call Us Now
            </button>
            <button style={{
              backgroundColor: 'transparent',
              color: '#7c3aed',
              border: '2px solid #7c3aed',
              padding: '12px 30px',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <Mail size={20} />
              Email Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;